import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import I18nHOC from "UIComponents/I18nHOC";
import { uploadFileV2 } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { DocEditor } from "@toddle-edu/ds-doc-editor";
import { FormInputError } from "@toddle-edu/ds-web";
import TheatreModeV2 from "UIComponents/TheatreModeV2/TheatreModeV2";
import { getCloudUrl } from "Constants";
import MathEquationEditor from "../MathEquationEditor";
import docEditorStore from "./docEditorStore";

const DEFAULT_EDITOR_WIDTH = 800;
import { allowURLWithCredentials } from "../../Utils/SecureUrlHelpers";
import AISpeechToText from "../../AppComponents/AIPromptBoxV3/components/AISpeechToText";

class DocEditorComponent extends UIBaseComponent {
  static openEquationEditorInstance = null;
  errorId = _.uniqueId("form-error-");

  state = {
    ...this.state,
    hasContent: false,
    isEditorLoading: false,
    uploadIds: [],
    showUploadConfirmation: false,
    pendingUnmountCallback: null,
  };
  containerRef = React.createRef();

  changeValue = (val, isEmpty) => {
    this.updateValue(val, isEmpty);
  };

  isValid = () => {
    const { error } = this.props;
    const errorMessage = error ? error : "Enter some content";

    if (!this.state.hasContent) {
      this.updateErrorState(errorMessage);
    } else {
      this.updateErrorState("");
    }
    return this.state.hasContent ? 0 : 1;
  };

  _onBlur = () => {
    const { value, shouldCallOnBlur } = this.props;
    if (shouldCallOnBlur) {
      this.onBlur({ value });
    }
  };

  closeEditor = () => {
    if (DocEditorComponent.openEquationEditorInstance === this) {
      DocEditorComponent.openEquationEditorInstance = null;
    }

    this.setState({
      isEquationEditorOpen: false,
      latex: "",
      latexList: [],
    });
  };

  handleInsertEquation = ({ height, width, latex, verticalAlign, src }) => {
    const equationAttributes = {
      src,
      height: height + "px",
      width: width + "px",
      verticalAlign: verticalAlign + "px",
      latex,
      altText: latex,
      display: "inline",
    };

    this.setState({ equationAttributes });
  };

  renderEquationEditor = () => {
    const {
      allowCopyPasteInEquationEditor = true,
      disabledCopyButtonTooltipText = null,
    } = this.props;
    const latexList = this.state.latexList;
    return (
      <MathEquationEditor
        closeEditor={this.closeEditor}
        onInsertButtonClick={this.handleInsertEquation}
        latex={this.state.latex}
        latexList={
          this.state.allowReInsertImageAutomation && latexList?.length > 0
            ? latexList
            : null
        }
        selectedText={
          this.state.allowEqCreationViaSelection && this.state.selectedText
            ? this.state.selectedText
            : null
        }
        allowCopyPasteInEquationEditor={allowCopyPasteInEquationEditor}
        disabledCopyButtonTooltipText={disabledCopyButtonTooltipText}
      />
    );
  };

  componentDidMount() {
    docEditorStore.setState(true);
  }

  componentWillUnmount() {
    docEditorStore.setState(false);
  }

  handleUploadStateChange = uploadIds => {
    this.setState({ uploadIds });

    // Notify parent component of upload state change
    const { onUploadStateChange } = this.props;
    if (onUploadStateChange) {
      onUploadStateChange(uploadIds);
    }
  };

  canSafelyUnmount = onConfirm => {
    if (this.state.uploadIds.length > 0) {
      this.setState({
        showUploadConfirmation: true,
        pendingUnmountCallback: onConfirm,
      });
      return false;
    }
    return true;
  };

  // Returns true if upload is currently in progress
  isUploadInProgress = () => this.state.uploadIds.length > 0;

  // Call this when user confirms they want to discard the upload and close
  confirmDiscard = () => {
    this.setState({ uploadIds: [] });
  };

  renderEdit = () => {
    const {
      height,
      width = 1400,
      value = "",
      disabled,
      readOnly,
      scrollToEnd = false,
      hideErrorMessage,
      placeholder = "",
      fontSize,
      isActive = false,
      i18n,
      viewOnly = false,
      config = {},
      saveDelay = 1000,
      containerStyle,
      minHeight,
      docEditorStyles = {},
      nodeConfig = {},
      isScrollHidden = true,
      shouldEditorFocusOnLoad = true,
      getUserData,
      onCommentAdd,
      onCommentDelete,
      onCommentUpdate,
      onCommentResolve,
      onCommentSuggestionAccept,
      onThreadUpdate,
      comments,
      isCommentLoaded,
      waitForCommentsToLoad,
      onCommentDraftUpdate,
      nodesDisabled,
      smartPlaceholderOptions,
      smartPlaceholderValuesMap,
      isMathsEquationEnabled = false,
      isTableSizeContained,
      tableMaxWidth,
      extendedToolbarButtons,
      isExtendedToolbarEnabled,
      isCommentView,
      onlyAllow = [],
      textFormattingDisabled = {},
      staticFontSize,
      onUploadMedia,
      isInitialContentChangeIgnored = false,
      defaultTagSizes = {},
      isDropdownBodyLevel = false,
      isCompact = false,
      compactVariantMinHeight,
      isHeightAdaptive = false,
      showDragHandles = true,
      compactVariantStyles = {},
      tableDefaultWidth,
      inlineComments,
      onInlineCommentAdd,
      onInlineCommentUpdate,
      onInlineCommentDelete,
      onInlineCommentResolve,
      isQuickCommentEnabled,
      onQuickCommentsDataUpdate,
      quickCommentsData,
      commentListType,
      onCommentListTypeChange,
      inlineCommentColors,
      isCommentPasteDisabled,
      isNewCommentUIEnabled,
      commentUpdateId,
      onCommentArrayUpdate,
      commentConfig,
      className: customClassName,
      overrideElements,
      fontSizeOverride,
      fontSizeClampMode,
      headingPasteMap,
      dragMenuConfig,
    } = this.props;
    const errorMessage = this.state.error ? this.state.error : "";

    const handleMathsEditorOpen = () => {
      const equationEditorInstance =
        DocEditorComponent.openEquationEditorInstance;
      if (
        !this.state.isEquationEditorOpen &&
        equationEditorInstance &&
        equationEditorInstance !== this
      ) {
        equationEditorInstance.closeEditor();
      }

      if (!this.state.isEquationEditorOpen) {
        DocEditorComponent.openEquationEditorInstance = this;
      } else {
        DocEditorComponent.openEquationEditorInstance = null;
      }
      this.setState(prevState => ({
        isEquationEditorOpen: !prevState.isEquationEditorOpen,
        latex: "",
      }));
    };

    const handleUpload = async (...args) => {
      try {
        const result = await this.props.uploadFileV2(...args);
        if (onUploadMedia) {
          onUploadMedia(result, ...args);
        }
        return result;
      } catch (e) {
        console.error("Error uploading file:", e);
      }
    };

    const handleClearEquation = () =>
      this.setState({
        equationAttributes: "",
      });

    const handleDoubleClick = latexProps => {
      const equationEditorInstance =
        DocEditorComponent.openEquationEditorInstance;

      if (equationEditorInstance && equationEditorInstance !== this) {
        equationEditorInstance.closeEditor();
      }

      DocEditorComponent.openEquationEditorInstance = this;
      this.setState({
        latex: latexProps,
        isEquationEditorOpen: true,
      });
    };

    const mathsEditorConfig = isMathsEquationEnabled && {
      isOpen: this.state.isEquationEditorOpen,
      setIsMathsEditorOpen: handleMathsEditorOpen,
      equationAttributes: this.state.equationAttributes,
      clearEquation: handleClearEquation,
      onDoubleClick: handleDoubleClick,
    };

    return (
      <div ref={this.containerRef} style={containerStyle}>
        <DocEditor
          className={customClassName}
          readOnly={readOnly || disabled}
          scrollToEnd={scrollToEnd}
          lang={i18n?.language || "en"}
          height={height}
          width={width}
          content={value}
          onChange={this.changeValue}
          onFocus={e => this.onFocus({ value })}
          onBlur={this._onBlur}
          uploadToServer={handleUpload}
          hasContent={value =>
            this.setState({
              hasContent: value,
            })
          }
          placeholder={placeholder}
          getCloudUrl={getCloudUrl}
          uploadFile={handleUpload}
          TheatreMode={TheatreModeV2}
          fontSize={fontSize}
          isActive={isActive}
          viewOnly={viewOnly}
          config={config}
          saveDelay={saveDelay}
          minHeight={minHeight}
          styles={docEditorStyles}
          nodeConfig={nodeConfig}
          isScrollHidden={isScrollHidden}
          shouldEditorFocusOnLoad={shouldEditorFocusOnLoad}
          onCommentAdd={onCommentAdd}
          getUserData={getUserData}
          isCommentLoaded={isCommentLoaded}
          comments={comments}
          onCommentDelete={onCommentDelete}
          onCommentUpdate={onCommentUpdate}
          onCommentResolve={onCommentResolve}
          onCommentSuggestionAccept={onCommentSuggestionAccept}
          onThreadUpdate={onThreadUpdate}
          onCommentDraftUpdate={onCommentDraftUpdate}
          waitForCommentsToLoad={waitForCommentsToLoad}
          allowURLWithCredentials={allowURLWithCredentials}
          nodesDisabled={nodesDisabled}
          mathsEditorConfig={mathsEditorConfig}
          smartPlaceholderOptions={smartPlaceholderOptions}
          smartPlaceholderValuesMap={smartPlaceholderValuesMap}
          AISpeechToText={AISpeechToText}
          isTableSizeContained={isTableSizeContained}
          tableMaxWidth={tableMaxWidth}
          isExtendedToolbarEnabled={isExtendedToolbarEnabled}
          extendedToolbarButtons={extendedToolbarButtons}
          isCommentView={isCommentView}
          onlyAllow={onlyAllow}
          textFormattingDisabled={textFormattingDisabled}
          staticFontSize={staticFontSize}
          isInitialContentChangeIgnored={isInitialContentChangeIgnored}
          defaultTagSizes={defaultTagSizes}
          isDropdownBodyLevel={isDropdownBodyLevel}
          onUploadStateChange={this.handleUploadStateChange}
          isCompact={isCompact}
          isHeightAdaptive={isHeightAdaptive}
          showDragHandles={showDragHandles}
          compactVariantStyles={compactVariantStyles}
          compactVariantMinHeight={compactVariantMinHeight}
          tableDefaultWidth={tableDefaultWidth}
          inlineComments={inlineComments}
          onInlineCommentAdd={onInlineCommentAdd}
          onInlineCommentUpdate={onInlineCommentUpdate}
          onInlineCommentDelete={onInlineCommentDelete}
          onInlineCommentResolve={onInlineCommentResolve}
          isQuickCommentEnabled={isQuickCommentEnabled}
          onQuickCommentsDataUpdate={onQuickCommentsDataUpdate}
          quickCommentsData={quickCommentsData}
          commentListType={commentListType}
          onCommentListTypeChange={onCommentListTypeChange}
          inlineCommentColors={inlineCommentColors}
          isCommentPasteDisabled={isCommentPasteDisabled}
          isNewCommentUIEnabled={isNewCommentUIEnabled}
          commentUpdateId={commentUpdateId}
          onCommentArrayUpdate={onCommentArrayUpdate}
          commentConfig={commentConfig}
          overrideElements={overrideElements}
          fontSizeOverride={fontSizeOverride}
          fontSizeClampMode={fontSizeClampMode}
          headingPasteMap={headingPasteMap}
          dragMenuConfig={dragMenuConfig}
        />

        {isMathsEquationEnabled &&
          this.state.isEquationEditorOpen &&
          DocEditorComponent.openEquationEditorInstance === this &&
          this.renderEquationEditor()}

        <FormInputError
          id={this.errorId}
          {...(!_.isEmpty(errorMessage) &&
            !hideErrorMessage && { error: errorMessage })}
        />
      </div>
    );
  };
}

const mapActionCreators = {
  setToastMsg,
  uploadFileV2,
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  return {
    orgId: userInfo.org_id,
    userInfo,
  };
};

export default compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators, undefined, { withRef: true })
)(DocEditorComponent);

DocEditorComponent.propTypes = {
  ...UIBaseComponent.propTypes,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  scrollToEnd: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  placeholder: PropTypes.string,
  fontSize: PropTypes.string,
  isActive: PropTypes.bool,
  toolbarPosition: PropTypes.string,
  isHeightAdaptive: PropTypes.bool,
  isToolbarSticky: PropTypes.bool,
  shouldCallOnBlur: PropTypes.bool,
  containerStyle: PropTypes.object,
  onUploadStateChange: PropTypes.func,
  inlineComments: PropTypes.array,
  onInlineCommentAdd: PropTypes.func,
  onInlineCommentUpdate: PropTypes.func,
  onInlineCommentDelete: PropTypes.func,
  onInlineCommentResolve: PropTypes.func,
  isQuickCommentEnabled: PropTypes.bool,
  onQuickCommentsDataUpdate: PropTypes.func,
  quickCommentsData: PropTypes.array,
  commentListType: PropTypes.string,
  onCommentListTypeChange: PropTypes.func,
  inlineCommentColors: PropTypes.array,
  isCommentPasteDisabled: PropTypes.bool,
  isNewCommentUIEnabled: PropTypes.bool,
  commentUpdateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  overrideElements: PropTypes.shape({
    img: PropTypes.object,
    iframe: PropTypes.object,
    video: PropTypes.object,
    audio: PropTypes.object,
  }),
  fontSizeOverride: PropTypes.arrayOf(PropTypes.number),
  fontSizeClampMode: PropTypes.oneOf(["strict", "loose"]),
  headingPasteMap: PropTypes.objectOf(PropTypes.string),
  dragMenuConfig: PropTypes.shape({
    isEnabled: PropTypes.bool,
    offsetX: PropTypes.number,
    offsetY: PropTypes.number,
  }),
};

DocEditorComponent.defaultProps = {
  ...UIBaseComponent.defaultProps,
  height: 350,
  width: 864,
  shouldCallOnBlur: true,
  hideErrorMessage: false,
  placeholder: "",
  isActive: false,
  toolbarPosition: "top",
  isHeightAdaptive: false,
  isToolbarSticky: false,
  containerStyle: {},
};
